"use client";

import { VoicePreviewButton } from "@/components/audio/VoicePreviewButton";
import { cn } from "@/lib/utils";
import { ANALYTICS_EVENTS, track } from "@/lib/analyticsEvents";
import {
  AUDIO_VOICES,
  getAudioVoice,
  type AudioSegment,
} from "../../../shared/audio";
import {
  ArrowDown,
  ArrowUp,
  Plus,
  Trash2,
  Undo2,
  UserRound,
} from "lucide-react";
import { useMemo, useState } from "react";

export type AudioScriptEditType = "text" | "add_line" | "remove_line" | "reorder_line" | "undo";

export function AudioTranscriptEditor({
  segments,
  voiceMap,
  canUndo,
  onChange,
  onUndo,
  onVoiceChange,
  onSpeakerRename,
  onSpeakerAdded,
}: {
  segments: AudioSegment[];
  voiceMap: Record<string, string>;
  canUndo: boolean;
  onChange: (segments: AudioSegment[], editType: AudioScriptEditType) => void;
  onUndo: () => void;
  onVoiceChange: (speaker: string, voiceId: string) => void;
  onSpeakerRename: (speaker: string, nextName: string) => void;
  onSpeakerAdded: (speaker: string) => void;
}) {
  const [newSpeaker, setNewSpeaker] = useState("");
  const speakers = useMemo(
    () => Array.from(new Set(segments.map((segment) => segment.speaker))),
    [segments],
  );

  const update = (index: number, patch: Partial<AudioSegment>, type: AudioScriptEditType) => {
    onChange(segments.map((segment, current) => current === index ? { ...segment, ...patch } : segment), type);
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= segments.length) return;
    const next = [...segments];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next, "reorder_line");
  };

  const addSpeaker = () => {
    const speaker = newSpeaker.replace(/\s+/g, " ").trim().slice(0, 40);
    if (!speaker || speakers.includes(speaker)) return;
    onSpeakerAdded(speaker);
    setNewSpeaker("");
  };

  return (
    <div className="space-y-6">
      <section aria-labelledby="speaker-voices-heading" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 id="speaker-voices-heading" className="text-sm font-semibold text-foreground">Speakers and voices</h3>
            <p className="mt-1 text-xs text-muted-foreground">Detected names are a starting point. Review them before generating.</p>
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <label htmlFor="new-audio-speaker" className="sr-only">New speaker name</label>
            <input
              id="new-audio-speaker"
              value={newSpeaker}
              onChange={(event) => setNewSpeaker(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addSpeaker();
                }
              }}
              placeholder="e.g. Student 2"
              className="h-11 min-w-0 rounded-lg border border-input bg-card px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-success"
            />
            <button type="button" onClick={addSpeaker} disabled={!newSpeaker.trim()} className="inline-flex h-11 shrink-0 items-center gap-2 rounded-lg border border-border px-3 text-xs font-semibold text-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success disabled:cursor-not-allowed disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {speakers.map((speaker, index) => {
            const voiceId = voiceMap[speaker] ?? AUDIO_VOICES[index % AUDIO_VOICES.length]!.id;
            const voice = getAudioVoice(voiceId) ?? AUDIO_VOICES[0]!;
            return (
              <fieldset key={speaker} className="min-w-0 rounded-xl border border-border bg-muted/15 p-3">
                <legend className="sr-only">Voice for {speaker}</legend>
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success/10 text-success" aria-hidden><UserRound className="h-4 w-4" /></span>
                  <label className="min-w-0 flex-1">
                    <span className="sr-only">Speaker name</span>
                    <input
                      defaultValue={speaker}
                      onBlur={(event) => {
                        const next = event.target.value.replace(/\s+/g, " ").trim();
                        if (next && next !== speaker) onSpeakerRename(speaker, next);
                      }}
                      className="h-10 w-full rounded-lg border border-transparent bg-transparent px-2 text-sm font-semibold text-foreground outline-none hover:border-border focus-visible:border-border focus-visible:ring-2 focus-visible:ring-success"
                    />
                  </label>
                </div>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <label className="min-w-0 flex-1">
                    <span className="sr-only">Voice for {speaker}</span>
                    <select
                      value={voiceId}
                      onChange={(event) => onVoiceChange(speaker, event.target.value)}
                      className="h-11 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-success"
                    >
                      {AUDIO_VOICES.map((option) => (
                        <option key={option.id} value={option.id}>{option.name} · {option.accent}</option>
                      ))}
                    </select>
                  </label>
                  <VoicePreviewButton voiceId={voiceId} />
                </div>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{voice.description}</p>
              </fieldset>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="transcript-heading" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 id="transcript-heading" className="text-sm font-semibold text-foreground">Editable script</h3>
            <p className="mt-1 text-xs text-muted-foreground">Correct OCR, change speakers, or reorder lines without extracting again.</p>
          </div>
          <button type="button" onClick={onUndo} disabled={!canUndo} className="inline-flex h-11 items-center gap-2 rounded-lg border border-border px-3 text-xs font-semibold text-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success disabled:cursor-not-allowed disabled:opacity-50">
            <Undo2 className="h-3.5 w-3.5" /> Undo
          </button>
        </div>

        <ol className="space-y-3">
          {segments.map((segment, index) => (
            <li key={`${index}-${segment.speaker}`} className="rounded-xl border border-border bg-card p-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex items-center gap-2 sm:w-40 sm:shrink-0">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-foreground" aria-hidden>{index + 1}</span>
                  <label className="min-w-0 flex-1">
                    <span className="sr-only">Speaker for line {index + 1}</span>
                    <select value={segment.speaker} onChange={(event) => { const next = event.target.value; update(index, { speaker: next }, "text"); track(ANALYTICS_EVENTS.audioSpeakerChanged, { change_type: next === "Narrator" ? "narration" : "assign_line", speaker_count: speakers.length }); }} className="h-11 w-full rounded-lg border border-input bg-card px-2 text-sm font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-success">
                      {speakers.map((speaker) => <option key={speaker} value={speaker}>{speaker}</option>)}
                      {!speakers.includes("Narrator") ? <option value="Narrator">Narrator</option> : null}
                    </select>
                  </label>
                </div>
                <label className="min-w-0 flex-1">
                  <span className="sr-only">Spoken text for line {index + 1}</span>
                  <textarea value={segment.text} onChange={(event) => update(index, { text: event.target.value }, "text")} rows={2} className="min-h-20 w-full resize-y rounded-lg border border-input bg-card px-3 py-2 text-sm leading-relaxed text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-success" />
                </label>
                <div className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move line ${index + 1} up`} className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success disabled:cursor-not-allowed disabled:opacity-35"><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" onClick={() => move(index, 1)} disabled={index === segments.length - 1} aria-label={`Move line ${index + 1} down`} className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success disabled:cursor-not-allowed disabled:opacity-35"><ArrowDown className="h-4 w-4" /></button>
                  <button type="button" onClick={() => onChange(segments.filter((_, current) => current !== index), "remove_line")} disabled={segments.length === 1} aria-label={`Remove line ${index + 1}`} className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border text-muted-foreground hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive disabled:cursor-not-allowed disabled:opacity-35"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </li>
          ))}
        </ol>

        <button type="button" onClick={() => onChange([...segments, { speaker: speakers[0] ?? "Narrator", text: "" }], "add_line")} className={cn("inline-flex min-h-11 items-center gap-2 rounded-xl border border-dashed border-border px-4 text-sm font-semibold text-foreground hover:border-success/40 hover:bg-success/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success")}>
          <Plus className="h-4 w-4 text-success" /> Add dialogue turn
        </button>
      </section>
    </div>
  );
}
